import { useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { X, Sun, Moon, BookOpen, Monitor, Settings, LogOut, User } from 'lucide-react'
import { useTheme } from '../../providers/ThemeProvider'
import { useSelector } from 'react-redux'
import type { RootState } from '../../../store'
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock'

interface MobileOverflowSheetProps {
  open: boolean
  onClose: () => void
  onLogout: () => void
}

export function MobileOverflowSheet({ open, onClose, onLogout }: MobileOverflowSheetProps) {
  const { isDark, isDiary, toggleDarkMode, setThemeStyle } = useTheme()
  const { user } = useSelector((state: RootState) => state.auth)
  useBodyScrollLock(open)

  const sheetRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key === 'Tab' && sheetRef.current) {
        const focusable = sheetRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    },
    [onClose],
  )

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement
      document.addEventListener('keydown', handleKeyDown)
      requestAnimationFrame(() => {
        const first = sheetRef.current?.querySelector<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )
        first?.focus()
      })
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus()
      previousFocusRef.current = null
    }
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, handleKeyDown])

  return (
    <div
      ref={sheetRef}
      role="dialog"
      aria-modal="true"
      aria-label="Quick settings"
      className={`fixed inset-0 z-50 flex items-end transition-opacity duration-200 ${
        open ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      <div
        className={`relative w-full bg-surface rounded-t-2xl shadow-xl transition-transform duration-200 ${
          open ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)', maxHeight: '70vh' }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full bg-soft" />
        </div>

        <div className="flex items-center justify-between px-4 pb-2">
          <div className="flex items-center gap-2">
            {user?.avatar ? (
              <img src={user.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-brand flex items-center justify-center">
                <User className="h-4 w-4 text-on-brand" />
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-primary">
                {user?.preferredName || user?.username || 'User'}
              </p>
              <p className="text-xs text-muted">{user?.email}</p>
            </div>
          </div>
          {/* 44px touch floor (2026-08-29) */}
          <button
            onClick={onClose}
            aria-label="Close quick settings"
            className="inline-flex items-center justify-center min-h-touch min-w-touch rounded-lg hover:bg-surface-alt"
          >
            <X className="h-5 w-5 text-muted" />
          </button>
        </div>

        <div className="border-t border-soft px-4 py-3 space-y-1">
          {/* Theme row */}
          <div className="flex items-center gap-2 py-2">
            <button
              onClick={() => { setThemeStyle(isDiary ? 'modern' : 'diary'); onClose() }}
              className="flex-1 flex items-center justify-center gap-1.5 min-h-touch text-sm rounded-lg bg-surface-alt text-secondary"
            >
              {isDiary ? <Monitor className="h-4 w-4" /> : <BookOpen className="h-4 w-4" />}
              {isDiary ? 'Modern' : 'Diary'}
            </button>
            <button
              onClick={() => { toggleDarkMode(); onClose() }}
              className="flex-1 flex items-center justify-center gap-1.5 min-h-touch text-sm rounded-lg bg-surface-alt text-secondary"
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              {isDark ? 'Light' : 'Dark'}
            </button>
          </div>

          <Link
            to="/profile"
            onClick={onClose}
            className="flex items-center gap-3 px-3 py-2.5 min-h-touch text-sm rounded-lg text-secondary hover:bg-surface-alt"
          >
            <Settings className="h-4 w-4" />
            Settings
          </Link>

          <button
            onClick={() => { onClose(); onLogout() }}
            className="w-full flex items-center gap-3 px-3 py-2.5 min-h-touch text-sm rounded-lg text-bad hover:bg-bad/10"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      </div>
    </div>
  )
}
